import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  Skeleton,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  CalendarRange,
  ExternalLink,
  FileText,
  HelpCircle,
  Paperclip,
  Pencil,
  UserCheck,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { config } from "@/shared/lib/config";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { useLeaveRequestQuery } from "../api/queries";
import { HrTour } from "../tour/HrTour";
import { leaveRequestDetailTourSteps } from "../tour/hrTourSteps";
import { useHrTour } from "../tour/useHrTour";

const STATUS_LABELS: Record<string, string> = {
  pending_line: "ລໍຖ້າຫົວໜ້າສາຍ",
  pending_hr: "ລໍຖ້າ HR ອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

function statusBadgeVariant(
  status: string,
): "default" | "destructive" | "secondary" {
  if (status === "approved") return "default";
  if (status === "rejected") return "destructive";
  return "secondary";
}

export function LeaveRequestDetailPage() {
  const { id } = useParams({ from: "/app/hr/leave/requests/$id" });
  const navigate = useNavigate();
  const { data, isLoading } = useLeaveRequestQuery(id ?? "");
  const tour = useHrTour("hr-leave-detail-tour-completed");

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2">
              <Skeleton className="h-8 w-64 max-w-full" />
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-9 w-28" />
              <Skeleton className="h-9 w-24" />
            </div>
          </div>
          <div className="mx-auto w-full max-w-2xl space-y-4">
            <Skeleton className="h-36 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        </Main>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ບໍ່ພົບຄຳຂໍລານີ້.</p>
        </Main>
      </>
    );
  }

  const dateRangeLabel = `${formatDateLocal(data.startDate)} — ${formatDateLocal(data.endDate)}`;

  return (
    <>
      <Header />
      <Main>
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight sm:text-3xl">
                ລາຍລະອຽດຄຳຂໍລາ
              </h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-2 max-w-xl text-muted-foreground">
              <span className="font-medium text-foreground">
                {data.leaveTypeName}
              </span>
              <span className="mx-1.5 text-border">·</span>
              <span>{dateRangeLabel}</span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate({ to: "/app/hr/leave/requests" })}
            >
              <ArrowLeft className="size-4" />
              ກັບຄືນ
            </Button>
            {data.status === "pending_line" && (
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() =>
                  navigate({
                    to: "/app/hr/leave/requests/$id/edit",
                    params: { id: data.id },
                  })
                }
                data-tourid="hr-leave-detail-edit-button"
              >
                <Pencil className="size-4" />
                ແກ້ໄຂ
              </Button>
            )}
          </div>
        </div>

        <div className="mx-auto w-full max-w-2xl space-y-6">
          <div data-tourid="hr-leave-detail-card">
            <Card className="gap-0 overflow-hidden border-border/80 p-0 shadow-sm">
              <CardHeader className="space-y-1 border-border/60 border-b bg-muted/30 py-4">
                <CardTitle className="font-semibold text-lg tracking-tight">
                  ສະຫຼຸບຄຳຂໍ
                </CardTitle>
                <CardDescription>
                  ກວດເບິ່ງຊ່ວງວັນທີ, ຈຳນວນວັນ, ແລະສະຖານະການອະນຸມັດ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 p-0">
                {data.status === "rejected" && data.rejectionReason ? (
                  <div className="border-border/60 border-b bg-destructive/5 px-6 py-4">
                    <Alert
                      variant="destructive"
                      className="border-destructive/40 bg-transparent"
                    >
                      <AlertTitle>ຄຳຂໍຖືກປະຕິເສດ</AlertTitle>
                      <AlertDescription className="mt-1 text-pretty">
                        {data.rejectionReason}
                      </AlertDescription>
                    </Alert>
                  </div>
                ) : null}

                <div className="px-6 pt-6">
                  <div className="flex flex-col gap-4 rounded-xl border border-primary/15 bg-gradient-to-br from-primary/5 via-muted/40 to-muted/20 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                    <div className="flex items-start gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <CalendarRange className="size-5" aria-hidden />
                      </div>
                      <div>
                        <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                          ຊ່ວງວັນທີລາ
                        </p>
                        <p className="mt-0.5 font-semibold text-base text-foreground tracking-tight sm:text-lg">
                          {dateRangeLabel}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <span className="inline-flex items-center rounded-full border border-border/80 bg-background/80 px-3 py-1 font-medium text-sm tabular-nums shadow-sm">
                        {data.days} ວັນ
                      </span>
                      <Badge
                        variant={statusBadgeVariant(data.status)}
                        className="px-2.5 py-0.5"
                      >
                        {STATUS_LABELS[data.status] ?? data.status}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Separator className="bg-border/60" />

                <dl className="grid gap-3 px-6 sm:grid-cols-2 sm:gap-4">
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                      <FileText className="size-4" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ປະເພດການລາ
                      </dt>
                      <dd className="mt-1 font-medium leading-snug">
                        {data.leaveTypeName}
                      </dd>
                    </div>
                  </div>
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                      <CalendarRange className="size-4" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ວັນທີເລີ່ມ – ສິ້ນສຸດ
                      </dt>
                      <dd className="mt-1 font-medium leading-snug">
                        {dateRangeLabel}
                      </dd>
                    </div>
                  </div>
                  <div className="flex gap-3 rounded-lg border border-border/50 bg-muted/20 p-4 sm:col-span-2">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground shadow-sm">
                      <UserCheck className="size-4" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ການອະນຸມັດ
                      </dt>
                      <dd className="mt-1 space-y-1 text-sm leading-relaxed">
                        {data.status === "approved" && data.approvedAt ? (
                          <>
                            <p className="font-medium text-foreground">
                              ອະນຸມັດເມື່ອ {formatDateTimeLocal(data.approvedAt)}
                            </p>
                            {(data.approvedByName ?? data.approvedBy) ? (
                              <p className="text-muted-foreground">
                                ໂດຍ HR:{" "}
                                <span className="text-foreground">
                                  {data.approvedByName ?? data.approvedBy}
                                </span>
                              </p>
                            ) : null}
                          </>
                        ) : data.status === "pending_line" ? (
                          <span className="font-medium text-muted-foreground">
                            ລໍຖ້າຫົວໜ້າສາຍງານອະນຸມັດ
                          </span>
                        ) : data.status === "pending_hr" ? (
                          <span className="space-y-1">
                            {data.lineApprovedAt ? (
                              <p className="font-medium text-foreground">
                                ຫົວໜ້າສາຍອະນຸມັດແລ້ວ{" "}
                                {formatDateTimeLocal(data.lineApprovedAt)}
                                {data.lineApprovedByName ? (
                                  <> ( {data.lineApprovedByName} )</>
                                ) : null}
                              </p>
                            ) : null}
                            <span className="block font-medium text-muted-foreground">
                              ລໍຖ້າ HR ອະນຸມັດຂັ້ນສຸດທ້າຍ
                            </span>
                          </span>
                        ) : (
                          <span className="font-medium text-muted-foreground">
                            —
                          </span>
                        )}
                      </dd>
                    </div>
                  </div>
                </dl>

                {data.reason ? (
                  <>
                    <Separator className="bg-border/60" />
                    <div className="px-6 pb-2">
                      <div className="rounded-xl border border-border/60 bg-muted/25 p-4 sm:p-5">
                        <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ເຫດຜົນ
                        </p>
                        <p className="mt-2 whitespace-pre-wrap text-pretty text-foreground text-sm leading-relaxed">
                          {data.reason}
                        </p>
                      </div>
                    </div>
                  </>
                ) : null}

                {data.attachmentKey ? (
                  <>
                    <Separator className="bg-border/60" />
                    <div className="px-6 pb-6">
                      <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ເອກະສານຄັດຕິດ
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 gap-2"
                        onClick={() => {
                          window.open(
                            `${config.apiUrl}/files/${data.attachmentKey}`,
                            "_blank",
                          );
                        }}
                      >
                        <Paperclip className="size-4 shrink-0" aria-hidden />
                        ເປີດ / ດາວໂຫຼດໄຟລ์
                        <ExternalLink
                          className="size-3.5 shrink-0 opacity-70"
                          aria-hidden
                        />
                      </Button>
                    </div>
                  </>
                ) : null}

                <div className="border-border/60 border-t bg-muted/20 px-6 py-3">
                  <p className="text-center text-muted-foreground text-xs">
                    ສ້າງເມື່ອ {formatDateTimeLocal(data.createdAt)}
                    {data.updatedAt !== data.createdAt ? (
                      <> · ອັບເດດລ່າສຸດ {formatDateTimeLocal(data.updatedAt)}</>
                    ) : null}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-center pb-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => navigate({ to: "/app/hr/leave/requests" })}
            >
              <ArrowLeft className="mr-1 size-4" />
              ກັບໄປລາຍການຄຳຂໍລາ
            </Button>
          </div>

          <HrTour
            steps={leaveRequestDetailTourSteps}
            run={tour.run}
            onCallback={tour.handleJoyrideCallback}
          />
        </div>
      </Main>
    </>
  );
}
